import { ApiError, type OutgoingResponse } from '@sciolingo/api-client'
import { api } from '../api'
import { SYNC } from '../shared/config'
import { META_KEYS, db, metaSet } from '../db/db'
import type { LocalResponse, LocalSession } from '../db/types'

/**
 * The sync engine of §12.
 *
 * Its correctness rests on one server-side property: `POST /responses` is
 * idempotent on the client-generated response id (§7.3). That is what makes a
 * retry after an unknown outcome safe, and therefore what makes this queue
 * correct rather than merely hopeful.
 *
 * §12.4 is the limitation it cannot engineer around: Background Sync is
 * Chromium-only, so on iOS unsent responses wait until the student next opens
 * the app, and a session completed offline and never reopened never syncs. The
 * only available mitigations are flushing aggressively while the app *is* open
 * and making the pending count impossible to miss (§10.5).
 */

export interface SyncCounts {
  pending: number
  inflight: number
  failed: number
  synced: number
}

type Listener = (counts: SyncCounts) => void

const listeners = new Set<Listener>()
let flushing = false
let debounceTimer: ReturnType<typeof setTimeout> | undefined

export function onSyncCounts(listener: Listener): () => void {
  listeners.add(listener)
  void publishCounts()
  return () => listeners.delete(listener)
}

export async function readSyncCounts(): Promise<SyncCounts> {
  const all = await db.responses.toArray()
  return {
    pending: all.filter((r) => r.syncState === 'pending').length,
    inflight: all.filter((r) => r.syncState === 'inflight').length,
    failed: all.filter((r) => r.syncState === 'failed').length,
    synced: all.filter((r) => r.syncState === 'synced').length,
  }
}

async function publishCounts(): Promise<void> {
  const counts = await readSyncCounts()
  for (const listener of listeners) listener(counts)
}

/** §12.1 trigger: immediately after each answer, debounced. */
export function scheduleFlush(delayMs: number = SYNC.debounceMs): void {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    debounceTimer = undefined
    void flush()
  }, delayMs)
}

/**
 * §12.1 triggers: app open, `online`, regained visibility, session completion.
 * Registered once from the app root.
 */
export function registerFlushTriggers(): () => void {
  const onOnline = (): void => void flush()
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') void flush()
  }

  window.addEventListener('online', onOnline)
  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('pageshow', onOnline)

  void flush() // app open

  return () => {
    window.removeEventListener('online', onOnline)
    document.removeEventListener('visibilitychange', onVisible)
    window.removeEventListener('pageshow', onOnline)
  }
}

export async function flush(): Promise<void> {
  if (flushing) return
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return

  flushing = true
  try {
    const due = (await db.responses.where('syncState').anyOf('pending', 'failed').toArray()).filter(
      (r) => (r.nextAttemptAt ?? 0) <= Date.now() && r.attempts < SYNC.maxAttempts,
    )

    const bySession = new Map<string, LocalResponse[]>()
    for (const r of due) {
      const bucket = bySession.get(r.sessionId)
      if (bucket) bucket.push(r)
      else bySession.set(r.sessionId, [r])
    }

    for (const [sessionId, responses] of bySession) {
      const session = await db.sessions.get(sessionId)
      if (!session) continue
      await flushSession(session, responses)
    }

    await metaSet(META_KEYS.lastSyncAt, Date.now())
  } finally {
    flushing = false
    await publishCounts()
  }
}

async function flushSession(session: LocalSession, responses: LocalResponse[]): Promise<void> {
  const ids = responses.map((r) => r.id)
  await db.responses.where('id').anyOf(ids).modify({ syncState: 'inflight' })
  await publishCounts()

  const payload: OutgoingResponse[] = responses.map((r) => ({
    id: r.id,
    itemId: r.itemId,
    // The version the student actually answered against.
    itemVersion: r.itemVersion,
    answer: r.answer,
    answeredAt: r.answeredAt,
    timeSpentMs: r.timeSpentMs,
  }))

  try {
    const result = await api.submitResponses({
      sessionId: session.id,
      // The session grant, not the access token. A token expiring mid-test
      // must never be able to block submission.
      grantToken: session.grantToken,
      responses: payload,
    })

    // §7.3: acknowledged per element, so one rejection does not fail the batch.
    for (const ack of result.acks) {
      if (ack.status === 'accepted' || ack.status === 'duplicate') {
        await db.responses.update(ack.id, {
          syncState: 'synced',
          lastError: undefined,
          versionConflict: ack.versionConflict ?? false,
        })
        if (ack.versionConflict) {
          console.warn(
            `[sync] item version conflict on response ${ack.id}: answered against an older version ` +
              `than the server now holds. The server result governs (§5.3).`,
          )
        }
      } else {
        // A rejection is a permanent decision, not a transient failure — retrying
        // it would spin the queue forever, so it is parked and left diagnosable.
        await markFailed(ack.id, ack.reason ?? 'rejected', { permanent: true })
      }
    }
  } catch (error) {
    const retryable = error instanceof ApiError ? error.retryable : true
    const message = error instanceof Error ? error.message : String(error)
    for (const id of ids) await markFailed(id, message, { permanent: !retryable })
  }

  await publishCounts()
}

async function markFailed(id: string, reason: string, opts: { permanent: boolean }): Promise<void> {
  const existing = await db.responses.get(id)
  if (!existing) return

  const attempts = existing.attempts + 1
  await db.responses.update(id, {
    syncState: 'failed',
    attempts,
    lastError: reason,
    // §12.2: exponential backoff with jitter, capped. `attempts` and `lastError`
    // persist on the record so a stuck queue is diagnosable rather than mysterious.
    nextAttemptAt: opts.permanent ? Number.MAX_SAFE_INTEGER : Date.now() + backoff(attempts),
  })
}

function backoff(attempts: number): number {
  const exponential = Math.min(SYNC.maxBackoffMs, SYNC.baseBackoffMs * 2 ** attempts)
  return Math.round(exponential / 2 + Math.random() * (exponential / 2))
}
